export type Platform = 'responsekit' | 'patrolschedule';

/**
 * Platform-specific terminology. ResponseKit is EMS/ambulance-flavored;
 * patrolschedule is ski-patrol-flavored. UI strings that would otherwise
 * hardcode EMS terms ("EMS credential", "crew", "driver", "medical director")
 * route through this map so the same components read correctly on both
 * platforms. Add a key here rather than branching on platform at each call site.
 */
export interface SiteTerms {
  /** A front-line member: "Crew" (EMS) vs "Patroller" (ski patrol). */
  member: string;
  members: string;
  /** The `crew` role's display label in the role picker. */
  roleCrew: string;
  /** The `medical_director` role's display label. */
  roleMedicalDirector: string;
  /** The credential/qualification concept: "EMS Certification" vs
   *  "Patrol Classification". Used for level dropdowns + column headers. */
  credential: string;
  /** Short form for table headers etc: "EMS Level" vs "Classification". */
  credentialLevel: string;
  credentialExpiry: string;
  /** The credential-type name a fresh tenant seeds / displays. */
  credentialTypeName: string;
  /** The "can operate the vehicle" attribute: "Driver"/"can drive" (EMS)
   *  vs "Transport"/"can transport" (ski patrol — Alpine-qualified). */
  transportBadge: string;   // e.g. "CAN DRIVE" -> "CAN TRANSPORT"
  transportAuthorized: string; // e.g. "Driver authorized" -> "Transport authorized"
  /** The staffing-floor concept: "crew minimum" vs "patrol minimum". */
  crewMinimum: string;
  /** Ordered credential ladder for this platform, lowest -> highest. */
  credentialLevels: string[];
}

export interface SiteConfig {
  platform: Platform;
  terms: SiteTerms;
  // Identity
  product: string;
  logoPre: string;
  logoAccent: string;
  appName: string;
  shortName: string;
  // Optional parent-brand byline ("By ResponseKit"), shown smaller and
  // right-aligned beneath the wordmark. Set only on sub-brands powered by
  // another product; omitted on ResponseKit's own brand.
  byline?: string;
  tagline: string;
  taglineSuffix: string;
  // Colors
  bg: string;
  accent: string;
  accentDim: string;
  accentColor: string;
  secondaryColor: string;
  text: string;
  textMuted: string;
  textDim: string;
  cardBg: string;
  border: string;
  // Typography
  radius: string;
  fontMono: string;
  fontSans: string;
  fontPrimary: string;
  fontLink: string;
}

export function getSiteConfig(): SiteConfig {
  const host = window.location.hostname.toLowerCase();
  const isPatrol = host === 'patrolschedule.com'
    || host.endsWith('.patrolschedule.com');
  if (isPatrol) {
    return {
      platform: 'patrolschedule',
      terms: {
        member: 'Patroller',
        members: 'Patrollers',
        roleCrew: 'Patroller',
        roleMedicalDirector: 'Patrol Director',
        credential: 'Patrol Classification',
        credentialLevel: 'Classification',
        credentialExpiry: 'Classification Expiry',
        credentialTypeName: 'Patrol Classification',
        transportBadge: 'CAN TRANSPORT',
        transportAuthorized: 'Transport authorized',
        crewMinimum: 'patrol minimum',
        // Lowest -> highest. Alpine Patroller is transport-capable.
        credentialLevels: ['New Applicant', 'Candidate', 'Patroller', 'Alpine Patroller'],
      },
      product: 'PatrolSchedule',
      logoPre: 'Patrol',
      logoAccent: 'Schedule',
      appName: 'PatrolSchedule',
      shortName: 'PatrolSchedule',
      byline: 'By ResponseKit',
      tagline: 'Ski Patrol Operations',
      taglineSuffix: 'Operations',
      bg: '#1a3a2a',
      accent: '#457b9d',
      accentColor: '#457b9d',
      accentDim: 'rgba(69,123,157,0.15)',
      secondaryColor: '#457b9d',
      text: '#f8f9fa',
      textMuted: '#c8cdd3',
      textDim: '#6b705c',
      cardBg: '#1f4432',
      border: 'rgba(255,255,255,0.08)',
      radius: '12px',
      fontMono: "'IBM Plex Mono', monospace",
      fontSans: "'DM Sans', sans-serif",
      fontPrimary: "'DM Sans', sans-serif",
      fontLink: 'https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,500;9..40,700&family=IBM+Plex+Mono:wght@400;600&display=swap',
    };
  }
  return {
    platform: 'responsekit',
    terms: {
      member: 'Crew',
      members: 'Crew',
      roleCrew: 'Crew',
      roleMedicalDirector: 'Medical Director',
      credential: 'EMS Certification',
      credentialLevel: 'EMS Level',
      credentialExpiry: 'EMS Expiry',
      credentialTypeName: 'EMS',
      transportBadge: 'CAN DRIVE',
      transportAuthorized: 'Driver authorized',
      crewMinimum: 'crew minimum',
      credentialLevels: ['EMR', 'EMT', 'AEMT', 'EMT-Intermediate', 'Paramedic'],
    },
    product: 'ResponseKit',
    logoPre: 'Response',
    logoAccent: 'Kit',
    appName: 'ResponseKit',
    shortName: 'ResponseKit',
    tagline: 'EMS Operations Portal',
    taglineSuffix: 'EMS Operations',
    bg: '#0d1b2a',
    accent: '#e63946',
    accentColor: '#e63946',
    accentDim: 'rgba(230,57,70,0.15)',
    secondaryColor: '#1d3557',
    text: '#f1faee',
    textMuted: '#8d99ae',
    textDim: '#4a5568',
    cardBg: '#162231',
    border: 'rgba(255,255,255,0.06)',
    radius: '4px',
    fontMono: "'JetBrains Mono', monospace",
    fontSans: "'Outfit', sans-serif",
    fontPrimary: "'Outfit', sans-serif",
    fontLink: 'https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600&family=Outfit:wght@300;500;700&display=swap',
  };
}
